import { useMutation, useQuery } from '@apollo/client/react';
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { AccessNotice } from '@/components/AccessNotice/AccessNotice';
import { Button } from '@/components/Button/Button';
import { Callout } from '@/components/Callout/Callout';
import { EmptyState } from '@/components/EmptyState/EmptyState';
import { Icon } from '@/components/Icon/Icon';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { RecordList } from '@/components/RecordList/RecordList';
import { Spinner } from '@/components/Spinner/Spinner';
import { TenantForm, type TenantDraft } from '@/components/TenantForm/TenantForm';
import { TextField } from '@/components/TextField/TextField';
import {
  CREATE_TENANT,
  TENANTS_QUERY,
  type CreateTenantResult,
  type TenantsResult,
} from '@/graphql/tenants';
import { friendlyError, isPermissionDenied } from '@/lib/keystone-errors';
import { displayName, matchesSearch, sortByName } from '@/lib/tenants';
import { useResourceAccess } from '@/lib/use-resource-access';
import { useToast } from '@/toast/useToast';

import styles from './TenantsContainer.module.css';

/** `/tenants`: every brokerage tenant, for staff holding `tenants` Read; Full adds one. */
export function TenantsContainer() {
  const toast = useToast();
  const navigate = useNavigate();
  const { access, refetch: recheckAccess } = useResourceAccess('tenants');
  const canRead = access === 'read' || access === 'full';
  const canEdit = access === 'full';

  const [search, setSearch] = useState('');
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const { data, loading, error } = useQuery<TenantsResult>(TENANTS_QUERY, {
    skip: !canRead,
    fetchPolicy: 'cache-and-network',
  });
  const [createTenant, { loading: saving }] = useMutation<CreateTenantResult>(CREATE_TENANT);

  const tenants = useMemo(
    () =>
      sortByName((data?.brokerages ?? []).filter((tenant) => matchesSearch(tenant, search))).map(
        (tenant) => ({
          id: tenant.id,
          title: displayName(tenant),
          meta: tenant.organization
            ? `Part of ${displayName(tenant.organization)}`
            : 'No organization',
        }),
      ),
    [data, search],
  );

  if (!canRead) {
    return <AccessNotice area="tenants" onRetry={recheckAccess} state={access as 'checking'} />;
  }

  const onCreate = async (draft: TenantDraft) => {
    setCreateError(null);
    try {
      const result = await createTenant({
        variables: { input: draft },
        refetchQueries: [TENANTS_QUERY],
      });
      setCreating(false);
      toast.success({ title: `${draft.name} added` });
      const id = result.data?.createTenant.id;
      if (id) {
        navigate(`/tenants/${id}`);
      }
    } catch (cause) {
      setCreateError(friendlyError(cause));
      if (isPermissionDenied(cause)) {
        recheckAccess();
      }
    }
  };

  const total = data?.brokerages.length ?? 0;
  const addButton = canEdit ? (
    <Button icon={<Icon name="plus" />} onClick={() => setCreating(true)}>
      Add tenant
    </Button>
  ) : null;

  return (
    <div className={styles.page}>
      <PageHeader
        actions={addButton}
        lede="Every brokerage tenant in Keystone, and the organization each sits under."
        title="Tenants"
      />

      {total > 0 ? (
        <div className={styles.filters}>
          <div className={styles.search}>
            <TextField
              label="Search"
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Name, slug or app-api id"
              type="search"
              value={search}
            />
          </div>
        </div>
      ) : null}

      {error && !data ? (
        <Callout tone="danger" title="Tenants couldn't be loaded">
          {friendlyError(error)}
        </Callout>
      ) : loading && !data ? (
        <p className={styles.loading} role="status">
          <Spinner /> Loading tenants…
        </p>
      ) : total === 0 ? (
        <EmptyState action={addButton} icon="buildings" title="No tenants yet">
          <p>Tenants you add appear here.</p>
        </EmptyState>
      ) : tenants.length === 0 ? (
        <EmptyState icon="magnifying-glass" title="No tenants match">
          <p>Try a different name, slug or id.</p>
        </EmptyState>
      ) : (
        <RecordList
          items={tenants}
          label="Tenants"
          renderLink={(item, children) => <Link to={`/tenants/${item.id}`}>{children}</Link>}
        />
      )}

      {creating ? (
        <TenantForm
          busy={saving}
          error={createError}
          onCancel={() => {
            setCreating(false);
            setCreateError(null);
          }}
          onSubmit={onCreate}
        />
      ) : null}
    </div>
  );
}
